import { useEffect, useMemo, useRef, useState } from "react";
import { Banknote, CheckCircle, ChevronDown, PackageCheck, PackagePlus, Truck } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { DispatchOrder } from "../../types/Dispatch";
import { isManagerPlus } from "../../access/positions";
import { useReadOnly } from "../../access/ReadOnly";
import { useAccess } from "../../access/AccessContext";

// The flagged card's Resolve control — a dropdown button. Clicking it
// opens the menu of resolutions (per Jack, 2026-09-02: the click must
// never navigate straight to the order any more):
//
//   Dispatch        — open the resolve/pack page (the old one-click)
//   Manually fulfil — record that the outstanding units actually went
//                     out in one of the parcels already sent
//   Refund          — refund the outstanding units from the list
//   Send replacement — raise a free replacement for the outstanding
//                     units AND close the flag, in one go (leaders'
//                     request, 2026-10-08: before this they raised it on
//                     Admin Orders, came back and ran a zero Refund)
//
// Each item follows its own access key (E9, 2026-09-23), so the matrix
// decides who may do what — the BE enforces the same split on POST
// /orders/:id/resolve (a refund needs the refund key):
//   Dispatch, Manually fulfil  → dispatch.flagged.dispatch
//   Refund                     → dispatch.flagged.refund
//   Send replacement           → admin.orders.replacement (the same key
//                                the Orders page uses — it ships free stock)
// Until the access context is authoritative the old rule applies
// (Manager+ sees everything). Manually fulfil and Send replacement both
// need something to have shipped, so they are hidden for orders flagged
// before packing. When only Dispatch remains there is nothing to choose,
// so the button navigates directly; with neither key the button is shown
// disabled and explains.

type Props = {
    order: DispatchOrder;
    onDispatch: () => void;
    onRefund: () => void;
    onManualShip: () => void;
    onReplace?: () => void;
};

export const userIsManagerPlus = (): boolean => {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return false;
        const u = JSON.parse(raw);
        return isManagerPlus(u?.position ?? u?.permissions);
    } catch {
        return false;
    }
};

const FlaggedResolveSplitButton = ({ order, onDispatch, onRefund, onManualShip, onReplace }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const { authoritative, canWrite } = useAccess();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    const legacyLeader = useMemo(userIsManagerPlus, []);
    const canDispatch = authoritative ? canWrite("dispatch.flagged.dispatch") : true;
    const canRefund = authoritative ? canWrite("dispatch.flagged.refund") : legacyLeader;
    const hasParcels = (order.parcels?.length ?? 0) > 0;
    const showDispatch = canDispatch;
    const showRefund = canRefund;
    const showManualShip = hasParcels && (authoritative ? canDispatch : legacyLeader);
    const canReplace = authoritative ? canWrite("admin.orders.replacement") : legacyLeader;
    const showReplace = !!onReplace && hasParcels && canReplace;
    const hasMenu = showRefund || showManualShip || showReplace;

    // Close on outside-click or Escape — same pattern as the Orders-page
    // toolbar actions menu.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!showDispatch && !hasMenu) {
        return (
            <button className="btn btn-success" disabled aria-disabled title={t("resolve_no_grant")}>
                <CheckCircle strokeWidth={3} size={16} />
                {t("resolve")}
            </button>
        );
    }

    if (!hasMenu) {
        return (
            <button className="btn btn-success" onClick={onDispatch}>
                <CheckCircle strokeWidth={3} size={16} />
                {t("resolve")}
            </button>
        );
    }

    const pick = (action: () => void) => { setOpen(false); action(); };

    return (
        <div className="dispatch-flagged-resolve-split" ref={wrapRef}>
            <button
                className="btn btn-success"
                onClick={() => setOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <CheckCircle strokeWidth={3} size={16} />
                {t("resolve")}
                <ChevronDown size={14} strokeWidth={3} />
            </button>
            {open && (
                <div className="dispatch-toolbar-actions-menu dispatch-flagged-resolve-menu" role="menu">
                    {showDispatch && (
                        <button
                            type="button"
                            role="menuitem"
                            className="dispatch-toolbar-actions-item"
                            onClick={() => pick(onDispatch)}
                        >
                            <PackageCheck size={14} />
                            <span>{t("resolve_menu_dispatch")}</span>
                        </button>
                    )}
                    {showManualShip && (
                        <button
                            type="button"
                            role="menuitem"
                            className="dispatch-toolbar-actions-item"
                            onClick={() => pick(onManualShip)}
                            disabled={readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            <Truck size={14} />
                            <span>{t("resolve_menu_manual_ship")}</span>
                        </button>
                    )}
                    {showReplace && (
                        <button
                            type="button"
                            role="menuitem"
                            className="dispatch-toolbar-actions-item"
                            onClick={() => pick(onReplace!)}
                            disabled={readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            <PackagePlus size={14} />
                            <span>{t("resolve_menu_replace")}</span>
                        </button>
                    )}
                    {showRefund && (
                        <button
                            type="button"
                            role="menuitem"
                            className="dispatch-toolbar-actions-item"
                            onClick={() => pick(onRefund)}
                            disabled={readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            <Banknote size={14} />
                            <span>{t("resolve_menu_refund")}</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default FlaggedResolveSplitButton;
